import { useState, useEffect, useMemo } from 'react'
import { MapPin, Search, Users, CheckCircle, ChevronDown, ChevronUp, Radio } from 'lucide-react'
import api from '@/services/api'
import toast from 'react-hot-toast'

interface Territory {
  id: number
  name: string
  code: string
  region: string
  country: string
  is_active: boolean
  scraper_enabled: boolean
  has_scraper: boolean
  last_scraped_at: string | null
}

interface ClientTerritory {
  client_id: number
  client_name: string
  trade_type: string
  assigned_from: string
}

const REGIONS = [
  'All Regions',
  'East Midlands',
  'East of England',
  'London',
  'North East',
  'North West',
  'South East',
  'South West',
  'West Midlands',
  'Yorkshire and The Humber',
]

export default function Territories() {
  const [territories, setTerritories] = useState<Territory[]>([])
  const [territoryClients, setTerritoryClients] = useState<Record<number, ClientTerritory[]>>({})
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedRegion, setSelectedRegion] = useState('All Regions')
  const [showAvailableOnly, setShowAvailableOnly] = useState(false)
  const [expandedTerritory, setExpandedTerritory] = useState<number | null>(null)

  useEffect(() => {
    fetchTerritories()
  }, [])

  const fetchTerritories = async () => {
    try {
      const response = await api.get('/api/v1/territories/?limit=500')
      setTerritories(response.data)
      
      // Fetch client assignments for each territory
      const clientMap: Record<number, ClientTerritory[]> = {}
      // We'll use a different endpoint - get all clients and their territories
      const clientsResponse = await api.get('/api/v1/clients/')
      
      for (const client of clientsResponse.data) {
        try {
          const terrResponse = await api.get(`/api/v1/clients/${client.id}/territories`)
          for (const terr of terrResponse.data) {
            if (!clientMap[terr.territory_id]) {
              clientMap[terr.territory_id] = []
            }
            clientMap[terr.territory_id].push({
              client_id: client.id,
              client_name: client.company_name,
              trade_type: client.trade_type,
              assigned_from: terr.assigned_from,
            })
          }
        } catch {
          // Ignore errors for individual clients
        }
      }
      setTerritoryClients(clientMap)
    } catch (error) {
      toast.error('Failed to load territories')
    } finally {
      setLoading(false)
    }
  }

  // Filter and search territories
  const filteredTerritories = useMemo(() => {
    return territories.filter(territory => {
      // Search filter
      if (searchQuery && !territory.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false
      }
      // Region filter
      if (selectedRegion !== 'All Regions' && territory.region !== selectedRegion) {
        return false
      }
      // Available only filter
      if (showAvailableOnly && territoryClients[territory.id]?.length > 0) {
        return false
      }
      return true
    })
  }, [territories, searchQuery, selectedRegion, showAvailableOnly, territoryClients])

  // Stats
  const stats = useMemo(() => {
    const total = territories.length
    const assigned = Object.keys(territoryClients).filter(id => territoryClients[Number(id)]?.length > 0).length
    const available = total - assigned
    const scrapersActive = territories.filter(t => t.has_scraper && t.scraper_enabled).length
    const scrapersAvailable = territories.filter(t => t.has_scraper).length
    const byRegion: Record<string, number> = {}
    territories.forEach(t => {
      byRegion[t.region] = (byRegion[t.region] || 0) + 1
    })
    return { total, assigned, available, scrapersActive, scrapersAvailable, byRegion }
  }, [territories, territoryClients])

  const formatTradeType = (type: string) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  if (loading) {
    return <div className="flex justify-center p-8">Loading territories...</div>
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-slate-100">Territories</h1>
      </div>

      {/* Stats Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="card p-4 flex items-center gap-3">
          <div className="p-2 bg-slate-700/50 rounded-lg">
            <MapPin className="w-5 h-5 text-slate-300" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-100">{stats.total}</p>
            <p className="text-xs text-slate-400">Total Territories</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="p-2 bg-emerald-500/20 rounded-lg">
            <CheckCircle className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-emerald-400">{stats.available}</p>
            <p className="text-xs text-slate-400">Available</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="p-2 bg-[#f39c13]/20 rounded-lg">
            <Users className="w-5 h-5 text-[#f39c13]" />
          </div>
          <div>
            <p className="text-2xl font-bold text-[#f39c13]">{stats.assigned}</p>
            <p className="text-xs text-slate-400">Assigned</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="p-2 bg-blue-500/20 rounded-lg">
            <Radio className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-blue-400">{stats.scrapersActive}</p>
            <p className="text-xs text-slate-400">Scrapers Active</p>
          </div>
        </div>
      </div>

      {/* Territory List */}
      <div className="card overflow-hidden">
        <div className="px-4 py-2 bg-slate-800/30 border-b border-slate-700/50 flex justify-between items-center">
          <span className="text-sm text-slate-400">
            Showing {filteredTerritories.length} of {territories.length} territories
          </span>
        </div>
        <table className="w-full">
          <thead className="bg-slate-800/50 border-b border-slate-700/50">
            <tr>
              <th className="text-left px-4 py-3">
                <div className="space-y-2">
                  <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Territory</span>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-500" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search..."
                      className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 pl-7 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-[#f39c13]"
                    />
                  </div>
                </div>
              </th>
              <th className="text-left px-4 py-3">
                <div className="space-y-2">
                  <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Region</span>
                  <select
                    value={selectedRegion}
                    onChange={(e) => setSelectedRegion(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200 focus:outline-none focus:border-[#f39c13]"
                  >
                    {REGIONS.map(region => (
                      <option key={region} value={region}>{region}</option>
                    ))}
                  </select>
                </div>
              </th>
              <th className="text-left px-4 py-3">
                <div className="space-y-2">
                  <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Status</span>
                  <select
                    value={showAvailableOnly ? 'available' : 'all'}
                    onChange={(e) => setShowAvailableOnly(e.target.value === 'available')}
                    className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200 focus:outline-none focus:border-[#f39c13]"
                  >
                    <option value="all">All</option>
                    <option value="available">Available Only</option>
                  </select>
                </div>
              </th>
              <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Assigned Clients</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Planning Feed</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/30">
            {filteredTerritories.map((territory) => {
              const clients = territoryClients[territory.id] || []
              const isExpanded = expandedTerritory === territory.id
              
              return (
                <tr 
                  key={territory.id} 
                  className="hover:bg-slate-800/30 transition-colors cursor-pointer"
                  onClick={() => setExpandedTerritory(isExpanded ? null : territory.id)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-slate-500" />
                      <span className="text-slate-200 font-medium">{territory.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-slate-400">{territory.region}</span>
                  </td>
                  <td className="px-4 py-3">
                    {clients.length === 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                        <CheckCircle className="w-3 h-3" />
                        Available
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-[#f39c13]/20 text-[#f39c13] border border-[#f39c13]/30">
                        <Users className="w-3 h-3" />
                        {clients.length} client{clients.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {clients.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {clients.slice(0, 3).map((client, idx) => (
                          <span 
                            key={idx}
                            className="text-xs px-2 py-0.5 bg-slate-700/50 text-slate-300 rounded"
                          >
                            {client.client_name} ({formatTradeType(client.trade_type)})
                          </span>
                        ))}
                        {clients.length > 3 && (
                          <span className="text-xs text-slate-500">+{clients.length - 3} more</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-slate-500">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {territory.has_scraper ? (
                      territory.scraper_enabled ? (
                        <div className="flex items-center gap-2">
                          <span className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                            <Radio className="w-3 h-3" />
                            Active
                          </span>
                          {territory.last_scraped_at && (
                            <span className="text-xs text-slate-500">
                              {new Date(territory.last_scraped_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                          <Radio className="w-3 h-3" />
                          Disabled
                        </span>
                      )
                    ) : (
                      <span className="text-xs text-slate-500">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {clients.length > 0 && (
                      isExpanded ? (
                        <ChevronUp className="w-4 h-4 text-slate-500" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-slate-500" />
                      )
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        
        {filteredTerritories.length === 0 && (
          <div className="text-center py-12">
            <MapPin className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-200 mb-2">No territories found</h3>
            <p className="text-slate-400">Try adjusting your search or filters.</p>
          </div>
        )}
      </div>
    </div>
  )
}
